<template>
  <main class="ticket-page container p-4 text-sp-primary">
    <section class="container p-0 text-sp-primary border border-white border-opacity-20 rounded-md bg-sp-secondary bg-opacity-70">
      <form @submit.prevent="submitForm">
        <div class="text-center text-xl p-4 my-2">
          {{ $t("submitRequest") }}
        </div>
        <div class="p-4">
          <label class="block">{{ $t("type") }}</label>
          <IssueType :issue-type="issueType" @update:issue-type="updateIssueType" />
        </div>

        <div class="p-4">
          <label class="block">{{ $t("subject") }}</label>
          <input 
            v-model="subject"
            type="text"
            class="block w-full bg-transparent border border-white border-opacity-20 rounded-md p-2
            focus:outline-none focus:border-sp-primary" 
          >
        </div>

        <div class="p-4">
          <label class="block">{{ $t("description") }}</label>
          <textarea 
            v-model="description"
            oninput='this.style.height = ""; this.style.height = this.scrollHeight + "px"'
            class="overflow-hidden description block w-full resize-none bg-transparent border border-white border-opacity-20 
            rounded-md p-2 focus:outline-none focus:border-sp-primary"
          >
          </textarea>
        </div>

        <div class="p-4">
          <label class="block">{{ $t("email") }}</label>
          <input 
            v-model="email"
            type="text"
            class="block w-full bg-transparent border border-white border-opacity-20 rounded-md 
            p-2 focus:outline-none focus:border-sp-primary"
          >
        </div>

        <FileUploader @files-updated="handleFileUpdates" />

        <div class="flex justify-center p-4 my-2">
          <button type="submit" :disabled="isLoading" class="submit-button py-2 px-4 border-0 
            duration-150 rounded-md"
          >
            <i class="ri-send-plane-2-fill mr-1"></i> {{ $t("send") }} 
          </button>
        </div>
      </form>
    </section>

    <Spinner v-if="isLoading" />
    <Modal v-if="activeModal" :message="$t(activeModal)" @closed="closeModal" />
  </main>
</template>

<script>
import { ref } from 'vue'
import { useTranslate } from '../composables/useTranslate'
import FileUploader from '../components/FileUploader.vue'
import IssueType from '../components/IssueType.vue'
import Spinner from '../components/Spinner.vue'
import Modal from '../components/Modal.vue'
import { useRouter } from 'vue-router'
import { getUserData } from '../modules/collectData.js'
import { useLogger } from '../composables/useLogger'
import { reportError } from '../utils/reporting'

export default {
  components: {
    FileUploader,
    Spinner,
    Modal,
    IssueType
  },
  setup() {
    const router = useRouter()
    const { t, getTranslation } = useTranslate()
    const { logClick } = useLogger()

    const issueType = ref(null)
    const subject = ref('')
    const description = ref('')
    const email = ref('')
    const selectedFiles = ref([])

    const isLoading = ref(false)
    const activeModal = ref(null)

    const closeModal = () => { activeModal.value = null }

    const submitForm = async () => {
      if (!validateFields() || !validateEmail()) { return }
      isLoading.value = true

      const { uid, iid, lang, platform, game, userAgent, localTime, signature } = getUserData()
      const apiUrl = import.meta.env.VITE_API_URL
      const url = apiUrl + 'forms/base' +
      `?uid=${uid}&iid=${iid}&platform=${platform}&game=${game}` +
      `&user_agent=${userAgent}${localTime}&user_language=${lang}&token=${signature}`

      const data = {
        'subject': subject.value,
        'description': description.value,
        'requesterEmail': email.value,
        'gameCategory': issueType.value,
        'files': selectedFiles.value
      }

      fetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
        .then(response => {
          if (!response.ok) {
            reportError(`ticket submit HTTP ${response.status}`, { source: 'RequestView.submitForm', status: response.status })
            router.push({ name: 'helpcenter' })
          }
          return response.json()
        })
        .then(data => {
          if (!data.isSuccess) {
            reportError('ticket submit: isSuccess=false', { source: 'RequestView.submitForm' })
            router.push({ name: 'helpcenter' })
            return
          }
          isLoading.value = false
          activeModal.value = 'messageSent'
        })
        .catch(error => {
          reportError(error, { source: 'RequestView.submitForm' })
          router.push({ name: 'helpcenter' })
        })

      issueType.value = null
      subject.value = ''
      description.value = ''
      email.value = ''
      logClick('request.ticketSubmitted', 'request.requestForm')
    }

    const updateIssueType = (newValue) => {
      issueType.value = newValue
    }

    const validateFields = () => {
      if (!issueType.value) {
        activeModal.value = 'issueEmpty'
        return false
      }

      if (subject.value.trim() === '' || subject.value.length < 5) {
        activeModal.value = 'subjEmpty'
        return false
      }

      if (description.value.trim() === '' || description.value.length < 5) {
        activeModal.value = 'descEmpty'
        return false
      }

      return true
    }

    const validateEmail = () => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (email.value.trim() === '' || !emailPattern.test(email.value)) {
        activeModal.value = 'emailInvalid'
        return false
      }
      return true
    }

    const handleFileUpdates = (fileTokens) => {
      selectedFiles.value = fileTokens
    }


    return { issueType, subject, description, email, isLoading, selectedFiles, activeModal,
      t, getTranslation, submitForm, updateIssueType, validateFields, validateEmail, handleFileUpdates, closeModal, logClick }
  }
}
</script>
